import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';

import {ServerEndpoints} from '../../../server.endpoints';

/**
 * The DNS provider an installation registers its company aliases at (#637).
 *
 * Carried as a lowercase **string**, not as an ordinal: it is stored that way in the `dns_provider`
 * environment setting, so a wire ordinal would have to be mapped twice and would silently renumber the day a
 * provider is inserted rather than appended.
 */
export type DnsProvider = 'none' | 'cloudflare';

/**
 * A ledger row's state.
 *
 * The ledger — not the zone — is the source of truth for the records this instance owns: `int`, `prev` and
 * `prod` share the zone `cheqme.app`, so a reconcile that acted on whatever it found there would delete
 * another environment's records. `pendingAdd`/`pendingRemove` are the two halves of that intent,
 * `registered` is the settled state and `failed` is an attempt that refused (its reason is in `lastError`).
 */
export type DnsEntryState = 'pendingAdd' | 'registered' | 'pendingRemove' | 'failed';

/**
 * One alias in the ledger.
 *
 * The wire row carries more than this — `provider`, `intent`, `attempts`, `lastAttemptUtc` — which the card
 * deliberately does not render: they are operator-console material, and a list that reports an attempt count
 * beside every alias buries the one thing a reader can act on. Declared here is what the screen uses.
 */
export interface DnsEntry {
  /** The company alias, lowercase — the label the `<alias>` and `*.<alias>` A records are named after. */
  alias: string;
  state: DnsEntryState;
  /**
   * The last failure's `E_DNS_*` code, or `null`. Preferred over {@link lastError} for display: it is what
   * the German copy is keyed on, and the style guide forbids rendering the server's own English.
   */
  lastErrorCode: string | null;
  /**
   * The provider's own reason, English. Shown only when {@link lastErrorCode} is missing or unknown to this
   * build — at which point an English diagnostic beats no reason at all.
   */
  lastError: string | null;
}

/**
 * What `/Management/Dns/GetProvider` answers — deliberately **not** the API token.
 *
 * `dns_cloudflare_api_token` is a catalogued secret (#637), so it is encrypted at rest and never leaves the
 * server; the screen reports *whether* a provider is configured and what it can see, never the credential.
 *
 * {@link mainHost} and {@link mainRecordFound} are the pair that decides whether anything can be registered
 * at all: zone and record target are **mirrored from the instance's own A record**, so without that record
 * there is nothing to copy — the card says so rather than letting every alias save fail silently later.
 */
export interface DnsProviderStatus {
  provider: DnsProvider;
  /** Whether the selected provider has usable credentials. Always `false` while `provider` is `'none'`. */
  configured: boolean;
  /** The platform setting „Öffentliche Adresse" reduced to its host, or `null` when it is not set. */
  mainHost: string | null;
  /** Whether an A record for {@link mainHost} could be read at the provider. */
  mainRecordFound: boolean;
  /** What that record points at — the address every alias record is given. `null` when it was not found. */
  mainRecordContent: string | null;
  /** The zone the records live in (longest-suffix match on {@link mainHost}), or `null`. */
  zoneName: string | null;
  registered: number;
  pending: number;
  failed: number;
  entries: DnsEntry[];
  /**
   * Why the card is blocked, when it is — the `E_DNS_*` the status probe itself ran into (main record
   * missing, zone missing, credentials rejected, provider unreachable), or `null`.
   *
   * It arrives on a **200**: failing the read would leave the operator with a screen that says nothing at all
   * about an installation that is, in fact, not registering anything.
   */
  error: DnsStatusError | null;
}

/** A structured reason as `BuildResponse` writes it. Only `code` is ever rendered — see the card. */
export interface DnsStatusError {
  code: string;
  /** The server's English, developer-facing sentence. **Never rendered.** */
  description: string;
  statusCode: number;
}

/**
 * The body of `PUT /Management/Dns/SetProvider`.
 *
 * `token` is **omitted** rather than sent blank when the operator did not type one: an omitted token means
 * "keep whatever is stored", while a blank one is refused with `E_DNS_PROVIDER_TOKEN_BLANK`. Sending `''` on
 * every save would therefore make it impossible to re-select Cloudflare without re-typing the credential.
 */
export interface DnsProviderRequest {
  provider: DnsProvider;
  token?: string;
}

/**
 * Reads and writes the installation-wide DNS provider (PlatformOwner only).
 *
 * Three calls, all answering the same {@link DnsProviderStatus}, which is what lets the card re-render from
 * the server's verdict rather than from what was typed:
 *
 * * `get()` — the status, on load;
 * * `set()` — switch provider and/or record a token. The server verifies the credentials **before** storing
 *   anything, so a refusal leaves the installation exactly as it was;
 * * `reconcile()` — drive every pending/failed ledger row once now, instead of waiting for the recurring
 *   `alias-dns-reconcile` job.
 */
@Injectable({providedIn: 'root'})
export class DnsHttpService {
  constructor(@Inject('BASE_URL') private baseUrl: string, private httpClient: HttpClient) {
  }

  /** The current provider, what it can see, and the ledger. */
  get(): Observable<DnsProviderStatus> {
    return this.httpClient.get<DnsProviderStatus>(
      `${this.baseUrl}${ServerEndpoints.Management_Dns_GetProvider}`);
  }

  /**
   * Selects the provider and, when one was typed, stores its token.
   *
   * @param provider the provider to run on.
   * @param token    the credential. A blank/absent value is **not sent at all** — see
   *                 {@link DnsProviderRequest}.
   */
  set(provider: DnsProvider, token?: string): Observable<DnsProviderStatus> {
    const body: DnsProviderRequest = token ? {provider, token} : {provider};
    return this.httpClient.put<DnsProviderStatus>(
      `${this.baseUrl}${ServerEndpoints.Management_Dns_SetProvider}`, body);
  }

  /** Drives every pending/failed ledger row once and answers the status afterwards. */
  reconcile(): Observable<DnsProviderStatus> {
    return this.httpClient.post<DnsProviderStatus>(
      `${this.baseUrl}${ServerEndpoints.Management_Dns_Reconcile}`, {});
  }
}
